import { defineStore } from "pinia";
import type { IAuthUserInfo } from "~/types/auth/user-info";
import api from "~/utils/auth/api";
import { clearTimer, setSessionLimit } from "~/composables/useSessionLimit";
import type { IGamingExclusion, ILimit } from "~/types/responsible-gaming/responsible-gaming";
import type { IUpdateDetailsSubmitData } from "~/types/wms-config-form/update-detail";
import type { IFicaStatusResponse } from "~/types/auth/fica";
import {
	initResponsibleGamingExcl,
	initResponsibleGamingLimits,
	isExclInitiated,
	isLimitsInitiated,
} from "~/utils/responsible-gaming/responsibleGamingUtils";
import { useSettings } from "./settings";
import { useBonusses } from "./bonusses";
import { setWildCardCookie, removeWildCardCookie } from "~/utils/cookie-util";

interface IAuth {
	token: string | null;
	refresh_token: string | null;
	cacheKey: number;
	loggedIn: boolean;
	hydrated: boolean;
	balance: IAuthUserInfo["accountBalances"] | null;
	balanceHidden: boolean;
	balanceRefreshing: boolean;
	profile: IAuthUserInfo | null;
	hasThreatMetricsInitialised: boolean;
	register: {
		step: number;
		success: boolean;
		data: {
			[x: number]: {
				[x: string]: any;
			};
		} | null;
		otpValue: string | null;
	};
	notifications: number;
	modals: {
		login: boolean;
		register: boolean;
		forgotPassword: boolean;
		notifications: boolean;
		balance: boolean;
		sumsub: boolean;
	};
	responsibleGaming: {
		limits: Map<"DEPOSITLIMIT" | "WAGERLIMIT" | "LOSSLIMIT", ILimit[]>;
		exclusions: Map<"SESSIONLIMIT" | "COOLOFF" | "SELFEXCLUSION", IGamingExclusion>;
	};
	betCount: number;
	isFicaRequiredForBet: boolean;
	welcomeOffer: {
		isAccepted: boolean;
		isDoItLater: boolean;
		isSuccessful: boolean;
	};
}

export const useAuth = defineStore({
	id: "auth",
	state: (): IAuth => ({
		token: null,
		refresh_token: null,
		cacheKey: 0,
		loggedIn: false,
		hydrated: false,
		balance: null,
		balanceHidden: false,
		balanceRefreshing: false,
		hasThreatMetricsInitialised: false,
		profile: null,
		register: {
			step: 1,
			success: false,
			data: null,
			otpValue: null,
		},
		notifications: 0,
		modals: {
			login: false,
			register: false,
			forgotPassword: false,
			notifications: false,
			balance: false,
			sumsub: false,
		},
		responsibleGaming: {
			limits: new Map(),
			exclusions: new Map(),
		},
		betCount: 0,
		isFicaRequiredForBet: false,
		welcomeOffer: {
			isAccepted: false,
			isDoItLater: false,
			isSuccessful: false,
		},
	}),
	actions: {
		async login(token: string, refresh_token: string) {
			await api<IAuthUserInfo>({
				url: "/users/userinfoextended",
				type: "auth",
				options: { method: "GET", headers: { Authorization: `Bearer ${token}` } },
			})
				.then(async (res) => {
					this.token = token;
					this.refresh_token = refresh_token;
					setWildCardCookie("betway-auth", token, 7);
					setWildCardCookie("betway-refresh-token", refresh_token, 7);
					setWildCardCookie("AccountId", res.id, 7);
					setWildCardCookie("PlayerId", res.id, 7);
					localStorage.setItem("prod_auth._token", token);
					this.loggedIn = true;
					this.profile = res;
					this.balance = res.accountBalances;
					this.modals.login = false;
					this.hydrated = true;
					const settings = useSettings();
					this.isFicaRequiredForBet = res.isIDVerificationOutstanding && settings.getCountryConfig?.countryIsoTwo === "ZA";

					if (settings.isNativeApp) {
						await this.updateBalance();

						useNuxtApp().$messageHandler({
							message: "Login",
							data: {
								login: true,
								PlayerId: res.id,
								setLang: useSettings().locale,
								token: `Bearer ${token}`,
								"refresh-token": refresh_token,
							},
						});
					}
					if (settings.hasFeature("IsVerifyAccountEnabled") && this.profile?.isRegistrationComplete !== true) {
						this.modals.register = true;
					}
					if (res.isIDVerificationOutstanding && settings.hasFeature("IsSumSubSdkEnabled")) {
						const sumsubResponse = await this.updateSumsubStatus();
						if (!sumsubResponse.responseMetadata.compliant) {
							this.modals.sumsub = true;
						}
					}
				})
				.catch(() => {
					this.hydrated = true;
					removeWildCardCookie("betway-auth");
					removeWildCardCookie("betway-refresh-token");
					localStorage.removeItem("prod_auth._token");
				});

			await initResponsibleGamingLimits();
			await initResponsibleGamingExcl();
		},
		async logout() {
			// Remove livechat session
			const settings = useSettings();

			try {
				if (settings.config?.devConfig && settings.config.devConfig.liveChat) {
					window?.__CONNEXONE_LIVECHAT__?.closeWidget();
					const liveChatUrl = new URL(settings.config?.devConfig.liveChat);
					const liveChatSearchParams = new URLSearchParams(liveChatUrl.searchParams);
					const liveChatId = liveChatSearchParams.get("connid");
					useCookie(`cnx_cx_livechat-betway${liveChatId}`).value = null;
					useCookie(`cnx_pp_livechat-betway${liveChatId}`).value = null;
					useCookie(`cnx_it_livechat-betway${liveChatId}`).value = null;

					document.getElementById("livechat-betway")?.remove();
					document.getElementById("cx-chat-btn")?.remove();
					document.getElementById("cx-nudge")?.remove();
					document.getElementById("cx-omni")?.remove();
					document.getElementById("cx-whatsapp-popover")?.remove();
					document.getElementById("cx-chat")?.remove();
					settings.liveChatLoaded = false;
				}
			} catch (e) {}

			setTimeout(() => {
				this.token = null;
				this.refresh_token = null;
				removeWildCardCookie("betway-auth");
				removeWildCardCookie("betway-refresh-token");
				localStorage.removeItem("prod_auth._token");
				this.loggedIn = false;
				this.profile = null;
				this.balance = null;
			}, 500);

			clearTimer();

			const nuxtApp = useNuxtApp();
			const auth = useAuth();

			nuxtApp.$messageHandler({
				message: "Login",
				data: {
					login: false,
					PlayerId: "",
					setLang: useSettings().locale,
					token: "",
				},
			});
			nuxtApp.$messageHandler({
				message: "Balance",
				data: {
					cashBalance: auth.profile?.accountBalances.cashBalance,
					freeBetBalance: auth.profile?.accountBalances?.bonusBalance,
					bonusBalance: auth.profile?.accountBalances?.ticketBalance,
				},
			});
			if (settings.config?.regionCode === "ZA") {
				const ifrm = document.createElement("iframe");
				ifrm.setAttribute("src", "https://www.betway.co.za/account/logout?v=3.2");
				ifrm.setAttribute("scrolling", "yes");
				ifrm.style.width = "0px";
				ifrm.style.height = "0px";
				ifrm.style.display = "none";
				document.body.prepend(ifrm);
				setTimeout(() => {
					ifrm.remove();
				}, 2000);
			}
		},
		resetRegistration() {
			this.modals.register = false;
			this.register = {
				step: 1,
				success: false,
				data: null,
				otpValue: null,
			};
			this.welcomeOffer = {
				isAccepted: false,
				isSuccessful: false,
				isDoItLater: false,
			};
		},
		async updateUser() {
			await api<IAuthUserInfo>({
				url: "/users/userinfoextended",
				type: "auth",
				options: { method: "GET" },
			}).then((res) => {
				setWildCardCookie("AccountId", res.id, 7);
				setWildCardCookie("PlayerId", res.id, 7);
				this.profile = { ...res, isNewUser: this.profile?.isNewUser || false };
				this.balance = res.accountBalances;
			});
		},
		async updateSumsubStatus() {
			const res = await api<IFicaStatusResponse>({
				url: "/Users/GetFicaStatus",
				type: "auth",
				options: { method: "POST" },
			});
			if (res.responseMetadata.processType === 2 && this.profile) {
				this.profile.isNewUser = true;
			}
			if (res.responseMetadata.compliant && this.profile) {
				this.profile.isIDVerificationOutstanding = false;
			} else if (!res.responseMetadata.compliant && this.profile) {
				this.profile.isIDVerificationOutstanding = true;
			}
			return res;
		},
		async updateBalance(indicator = false, bustCache = false) {
			if (indicator) this.balanceRefreshing = true;
			const settings = useSettings();

			if (bustCache) this.cacheKey++;

			const cacheBustString = `?t=${this.cacheKey}`;

			await api<IAuthUserInfo["accountBalances"]>({
				url: settings.config?.regionCode === "ZA" ? `/AccountBalance/AccountBalance${cacheBustString}` : `/users/accountbalance${cacheBustString}`,
				type: "auth",
				options: {
					method: "GET",
				},
			})
				.then(async (res) => {
					const bonusses = useBonusses();

					this.balance = res;
					this.balanceRefreshing = false;

					const currencySymbol = settings.currencyCode;

					useNuxtApp().$messageHandler({
						message: "Balance",
						data: {
							cashBalance: res.cashBalance,
							freeBetBalance: res.bonusBalance,
							bonusBalance: res.ticketBalance,
							sportBonusBalance: res.sportBonusBalance,
							playthroughBalance: res.playthroughBalance,
							sportBonusBalancePercent: bonusses.activeSportBonus
								? Math.round(bonusses.activeSportBonus?.playthroughProgressAmount / bonusses.activeSportBonus?.playthroughTargetAmount)
								: 0,
							playthroughBalancePercent: bonusses.casinoBonusSummary
								? Math.round(bonusses.casinoBonusSummary.playthroughTargetAmount / bonusses.casinoBonusSummary.playthroughTargetRemaining)
								: 0,
							sportInactivebonuses: bonusses.sportBonusSummaries?.filter((bonus) => bonus.status.toLowerCase() === "inactive"),
							currencySymbol,
							tickets: [
								{
									key: "betwayJackpot",
									count: res.betwayJackpotTicketBalance,
								},
								{
									key: "dailyJackpot",
									count: res.dailyJackpotTicketBalance,
								},
							],
						},
					});
				})
				.catch(() => {
					this.balanceRefreshing = false;
				});
		},
		editUser(data: IUpdateDetailsSubmitData, username: string) {
			return api({
				url: `/users/EditUser/${username}`,
				type: "auth",
				options: {
					method: "PUT",
					headers: {
						accept: " application/json, text/plain, */*",
					},
				},
				data,
			});
		},

		async responsibleGamingExclusions(exclusionTypes: ("SESSIONLIMIT" | "COOLOFF" | "SELFEXCLUSION")[]) {
			let paramCount = 0;

			const exclusionMappings = {
				SESSIONLIMIT: 1,
				COOLOFF: 2,
				SELFEXCLUSION: 4,
			};

			exclusionTypes.forEach((exclusion) => {
				paramCount = paramCount + exclusionMappings[exclusion];
			});

			await api<IGamingExclusion[]>({
				url: `/ResponsibleGaming/GetResponsibleGamingExclusions?features=${paramCount}`,
				type: "player",
				apiVersion: "v2",
				options: { method: "GET" },
			}).then((res) => {
				const activeExclusions = res.filter((excl) => excl.isActive);
				if (activeExclusions) {
					const sessionLimit = activeExclusions.find((excl) => excl.exclusionType === "SessionLimit");
					if (sessionLimit) {
						this.responsibleGaming.exclusions.set("SESSIONLIMIT", sessionLimit);
						isExclInitiated.value = true;
						setSessionLimit(sessionLimit);
					}
				}
			});
		},
		async responsibleGamingLimits(features: ("DEPOSITLIMIT" | "WAGERLIMIT" | "LOSSLIMIT")[]) {
			let paramCount = 0;

			const featureMappings = {
				DEPOSITLIMIT: 2,
				WAGERLIMIT: 1,
				LOSSLIMIT: 4,
			};

			features.forEach((feature) => {
				paramCount = paramCount + featureMappings[feature];
			});

			await api<ILimit[]>({
				url: `/ResponsibleGaming/GetResponsibleGamingLimits?features=${paramCount}`,
				type: "player",
				options: { method: "GET" },
				apiVersion: "v2",
			}).then((resp) => {
				const limits: { [key: string]: ILimit[] } = {
					WAGERLIMIT: [],
					DEPOSITLIMIT: [],
					LOSSLIMIT: [],
				};
				resp.forEach((limit) => {
					if (!limit.isDeleted) {
						limits[limit.limitType.toUpperCase()].push(limit);
					}
				});

				for (const [key, value] of Object.entries(limits)) {
					this.responsibleGaming.limits.set(key as "DEPOSITLIMIT" | "WAGERLIMIT" | "LOSSLIMIT", value);
				}
				isLimitsInitiated.value = true;
			});
		},
		increaseBetCount(increment: number) {
			this.betCount = this.betCount + increment;

			useNuxtApp().$messageHandler({
				message: "MyBetsCount",
				data: { Count: this.betCount },
			});
		},
		decreaseBetCount() {
			this.betCount--;

			useNuxtApp().$messageHandler({
				message: "MyBetsCount",
				data: { Count: this.betCount },
			});
		},
	},
	persist: {
		paths: ["balanceHidden", "cacheKey"],
		storage: persistedState.localStorage,
	},
});
